(() => {
  "use strict";

  function arproUi(key, fallback, values) {
    var translations = window.arproI18n;
    var template = translations && Object.prototype.hasOwnProperty.call(translations, key) && typeof translations[key] === "string" && translations[key]
      ? translations[key] : fallback;
    return String(template).replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, function (match, name) {
      return values && Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match;
    });
  }

  const initializedRoots = new WeakSet();
  const allowedTariffModes = new Set(["trad", "grossistes", "review"]);
  const onboardingHash = "#demande-acces-pro";
  const accessRequestPath = document.querySelector("[data-arpro-pro-onboarding]")?.dataset.arproAccessRequestPath ||
    (document.body?.dataset.arproRootUrl || "/").replace(/\/$/, "") + "/pages/demande-acces-professionnel";
  const isAccessRequestPage = () => window.location.pathname.replace(/\/$/, "") === accessRequestPath;
  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");
  const privacyBannerSelectors = [
    "#shopify-pc__banner",
    ".shopify-pc__banner__dialog",
    "[data-shopify-pc-banner]",
    "[data-shopify-cookie-banner]",
    "shopify-privacy-banner",
    "[role='alertdialog'][aria-label*='Cookie' i]",
    "[role='dialog'][aria-label*='confidentialité' i]"
  ];

  const readSessionMode = (key) => {
    try {
      const value = window.sessionStorage.getItem(key);
      return allowedTariffModes.has(value) ? value : "";
    } catch (_error) {
      return "";
    }
  };

  const writeSessionMode = (key, mode) => {
    if (!allowedTariffModes.has(mode)) return;

    try {
      // This preference is non-sensitive and cannot grant Shopify B2B access.
      window.sessionStorage.setItem(key, mode);
    } catch (_error) {
      // The form remains usable when storage is unavailable.
    }
  };

  const readSessionFlag = (key) => {
    try {
      return window.sessionStorage.getItem(key) === "1";
    } catch (_error) {
      return false;
    }
  };

  const writeSessionFlag = (key) => {
    try {
      // This flag stores UI dismissal only, never form values or PII.
      window.sessionStorage.setItem(key, "1");
    } catch (_error) {
      // Reopening on the next page remains a safe fallback.
    }
  };

  const clearSessionFlag = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // The request state remains conservative when storage is unavailable.
    }
  };

  const getTariffMode = (select) => {
    const option = select?.selectedOptions?.[0];
    const mode = option?.dataset.arproRequestedMode || "";
    return allowedTariffModes.has(mode) ? mode : "";
  };

  const emitTariffRequest = (root, mode, submitted = false) => {
    if (!allowedTariffModes.has(mode)) return;

    // This is an interface hint only. Shopify's company location remains the
    // sole authoritative source for access and contextual prices.
    document.documentElement.dataset.arproRequestedTariffMode = mode;
    document.documentElement.dataset.arproTariffRequestState = submitted ? "submitted" : "preference";
    root.dispatchEvent(
      new CustomEvent("arpro:tariff-requested", {
        bubbles: true,
        detail: { mode, submitted }
      })
    );
  };

  const normalizedDigits = (value) => value.replace(/\D/g, "");
  const normalizedCountry = (value) =>
    value
      .trim()
      .toLocaleLowerCase("fr")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const isFrenchCountry = (value) => {
    const country = normalizedCountry(value);
    return ["fr", "france", "republique francaise", "french republic"].includes(country)
      || country.startsWith("france ")
      || country.startsWith("republique francaise ")
      || country.startsWith("french republic ");
  };

  const vatPrefixForCountry = (value) => ({
    belgique: "BE", belgium: "BE", allemagne: "DE", germany: "DE",
    espagne: "ES", spain: "ES", italie: "IT", italy: "IT",
    "pays-bas": "NL", netherlands: "NL", luxembourg: "LU",
    portugal: "PT", irlande: "IE", ireland: "IE",
    "royaume-uni": "GB", "united kingdom": "GB", suisse: "CH", switzerland: "CH"
  })[normalizedCountry(value)] || "";

  // A checksum catches typing errors; it does not prove that an establishment exists.
  const passesLuhn = (digits) => {
    let sum = 0;
    for (let index = digits.length - 1, position = 0; index >= 0; index -= 1, position += 1) {
      let digit = Number(digits[index]);
      if (position % 2 === 1) digit = digit * 2 > 9 ? digit * 2 - 9 : digit * 2;
      sum += digit;
    }
    return sum % 10 === 0;
  };

  const isPlausibleSiret = (value) => {
    const digits = value.replace(/\s/g, "");
    return /^\d{14}$/.test(digits)
      && !/^(\d)\1{13}$/.test(digits)
      && passesLuhn(digits.slice(0, 9))
      // La Poste uses an exceptional control: sum of digits divisible by five.
      && (passesLuhn(digits) || (digits.startsWith("356000000")
        && [...digits].reduce((sum, digit) => sum + Number(digit), 0) % 5 === 0));
  };

  const findExactEstablishment = (payload, siret) => {
    if (!/^\d{14}$/.test(siret) || !Array.isArray(payload?.results)) return null;
    const companies = payload.results.filter((company) => company?.siren === siret.slice(0, 9));
    if (companies.length !== 1 || companies[0].etat_administratif !== "A") return null;
    const company = companies[0];
    const establishments = [company.siege, ...(Array.isArray(company.matching_etablissements) ? company.matching_etablissements : [])]
      .filter((establishment) => establishment?.siret === siret);
    if (!establishments.length || establishments.some((establishment) => establishment.etat_administratif !== "A")) return null;
    const establishment = establishments[0];
    const text = (value, maximum) => typeof value === "string" ? value.trim().slice(0, maximum) : "";
    const name = text(company.nom_complet, 255);
    if (!name) return null;
    return {
      company: name,
      address: text(establishment.adresse, 500),
      postalCode: text(establishment.code_postal, 20),
      city: text(establishment.libelle_commune, 100)
    };
  };

  const isVisible = (element) => {
    if (!(element instanceof Element) || element.hidden) return false;
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
  };

  const customerPrivacyNeedsAttention = () => {
    try {
      const customerPrivacy = window.Shopify?.customerPrivacy;
      if (typeof customerPrivacy?.shouldShowBanner === "function" && customerPrivacy.shouldShowBanner()) {
        return true;
      }
    } catch (_error) {
      // DOM detection below remains available when the privacy API is delayed.
    }

    return privacyBannerSelectors.some((selector) =>
      Array.from(document.querySelectorAll(selector)).some(isVisible)
    );
  };

  const initOnboarding = (root) => {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const dialog = root.querySelector("[data-arpro-onboarding-dialog]");
    const form = root.querySelector("form.arpro-onboarding__form");
    const formState = root.querySelector("[data-arpro-onboarding-form-state]");
    const reopenButton = root.querySelector("[data-arpro-onboarding-open]");
    const liveRegion = root.querySelector("[data-arpro-onboarding-live]");
    const errorSummary = root.querySelector("[data-arpro-onboarding-errors]");
    const successPanel = root.querySelector("[data-arpro-onboarding-success]");
    const tariffSelect = root.querySelector("[data-arpro-tariff-request]");
    const requestKind = root.querySelector("[data-arpro-request-kind]");
    const quoteSelectionField = root.querySelector("[data-arpro-quote-selection-field]");
    const quoteSelectionPreview = root.querySelector("[data-arpro-quote-selection-preview]");
    const quoteSelectionSummary = root.querySelector("[data-arpro-quote-selection-summary]");
    const countryInput = root.querySelector("[data-arpro-country]");
    const lookupContainer = root.querySelector("[data-arpro-company-lookup]");
    const lookupButton = root.querySelector("[data-arpro-lookup-button]");
    const lookupStatus = root.querySelector("[data-arpro-lookup-status]");
    const lookupFields = {
      company: root.querySelector("[data-arpro-company]"),
      address: root.querySelector("[data-arpro-address]"),
      postalCode: root.querySelector("[data-arpro-postal-code]"),
      city: root.querySelector("[data-arpro-city]")
    };
    const lookupFieldVersions = Object.fromEntries(Object.keys(lookupFields).map((key) => [key, 0]));
    Object.entries(lookupFields).forEach(([key, field]) => {
      const markEdited = () => { lookupFieldVersions[key] += 1; };
      field?.addEventListener("input", markEdited);
      field?.addEventListener("change", markEdited);
    });
    let lookupController = null;
    let lookupVersion = 0;
    const registrationInput = root.querySelector("[data-arpro-siren]");
    const vatInput = root.querySelector("[data-arpro-vat]");
    const registrationLabel = root.querySelector("[data-arpro-registration-label]");
    const registrationHint = root.querySelector("[data-arpro-registration-hint]");
    const registrationType = root.querySelector("[data-arpro-registration-type]");
    const phoneInput = root.querySelector("[data-arpro-phone]");
    const tariffStorageKey = root.dataset.arproTariffStorageKey || "arpro:tariff-requested:v1";
    const dismissedStorageKey = root.dataset.arproDismissedStorageKey || "arpro:onboarding-dismissed:v1";
    const submittedStorageKey = root.dataset.arproSubmittedStorageKey || "arpro:onboarding-submitted:v1";
    const hasSuccess = formState?.dataset.arproFormSuccess === "true";
    const hasErrors = formState?.dataset.arproFormErrors === "true";
    let previouslyFocused = null;
    let resumeTarget = "";
    let navigationPending = false;
    let pendingPrivacyTrigger = null;
    let privacyPoll = 0;
    let privacyPollAttempts = 0;

    if (!dialog || !formState) return;

    const announce = (message) => {
      if (!liveRegion) return;
      liveRegion.textContent = "";
      window.requestAnimationFrame(() => {
        liveRegion.textContent = message;
      });
    };

    const getFocusableElements = () =>
      Array.from(dialog.querySelectorAll(focusableSelector)).filter(isVisible);

    const focusInitialControl = () => {
      const target =
        errorSummary ||
        successPanel ||
        form?.querySelector("input:invalid, select:invalid, textarea:invalid") ||
        dialog.querySelector("input:not([type='hidden']), select, textarea, button");
      const keepCurrentScroll = window.matchMedia("(min-width: 52.01rem)").matches;
      target?.focus({ preventScroll: keepCurrentScroll });
    };

    const stopPrivacyWatch = () => {
      if (privacyPoll) window.clearInterval(privacyPoll);
      privacyPoll = 0;
      privacyPollAttempts = 0;
    };

    const showDialog = (trigger = null) => {
      if (dialog.open) return;
      pendingPrivacyTrigger = null;
      root.classList.remove("arpro-onboarding--waiting-for-consent");
      previouslyFocused = trigger instanceof HTMLElement
        ? trigger
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      try {
        if (typeof dialog.showModal === "function") {
          dialog.showModal();
        } else {
          dialog.setAttribute("open", "");
          dialog.setAttribute("role", "dialog");
          dialog.setAttribute("aria-modal", "true");
        }
      } catch (_error) {
        dialog.setAttribute("open", "");
      }

      document.documentElement.classList.add("arpro-onboarding-open");
      if (reopenButton) reopenButton.hidden = true;
      window.requestAnimationFrame(focusInitialControl);
    };

    const releaseAfterPrivacy = () => {
      if (!pendingPrivacyTrigger && !root.classList.contains("arpro-onboarding--waiting-for-consent")) return;
      if (customerPrivacyNeedsAttention()) return;
      const trigger = pendingPrivacyTrigger;
      stopPrivacyWatch();
      showDialog(trigger);
    };

    const startPrivacyWatch = () => {
      if (privacyPoll) return;
      privacyPoll = window.setInterval(() => {
        privacyPollAttempts += 1;
        if (!customerPrivacyNeedsAttention()) {
          releaseAfterPrivacy();
          return;
        }
        if (privacyPollAttempts >= 240) {
          stopPrivacyWatch();
          announce(arproUi("js_access_after_privacy_closed", "La demande d’accès s’ouvrira après la fermeture de la bannière de confidentialité."));
        }
      }, 500);
    };

    const requestOpen = (trigger = null) => {
      if (dialog.open) return;
      if (customerPrivacyNeedsAttention()) {
        pendingPrivacyTrigger = trigger instanceof HTMLElement ? trigger : reopenButton;
        root.classList.add("arpro-onboarding--waiting-for-consent");
        if (reopenButton) reopenButton.hidden = true;
        announce(arproUi("js_access_choose_privacy_first", "Choisissez d’abord vos préférences de confidentialité. La demande d’accès professionnel s’ouvrira ensuite."));
        startPrivacyWatch();
        return;
      }
      stopPrivacyWatch();
      showDialog(trigger);
    };

    const scrollToTarget = (selector) => {
      if (!selector || !selector.startsWith("#")) return;
      const target = document.querySelector(selector);
      if (!target) {
        if (selector === "#catalogue-pro" && root.dataset.arproCatalogueUrl) {
          const destination = new URL(root.dataset.arproCatalogueUrl, window.location.origin);
          if (destination.origin === window.location.origin) window.location.assign(destination.href);
        }
        return;
      }
      target.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start"
      });
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    };

    const closeDialog = (target = "", options = {}) => {
      const { markDismissed = true, restoreFocus = true } = options;
      resumeTarget = target;
      navigationPending = !restoreFocus;
      pendingPrivacyTrigger = null;
      stopPrivacyWatch();
      root.classList.remove("arpro-onboarding--waiting-for-consent");
      if (markDismissed) writeSessionFlag(dismissedStorageKey);

      if (dialog.open && typeof dialog.close === "function") {
        dialog.close();
      } else {
        dialog.removeAttribute("open");
        dialog.dispatchEvent(new Event("close"));
      }
    };

    const markDismissedForNavigation = () => {
      writeSessionFlag(dismissedStorageKey);
      if (dialog.open) closeDialog("", { markDismissed: false, restoreFocus: false });
    };

    root.querySelectorAll("[data-arpro-onboarding-close]").forEach((button) => {
      button.addEventListener("click", () => closeDialog());
    });

    root.querySelectorAll("[data-arpro-onboarding-resume]").forEach((button) => {
      button.addEventListener("click", () => closeDialog(button.dataset.arproResumeTarget || "#accueil-pro"));
    });

    reopenButton?.addEventListener("click", () => window.location.assign(accessRequestPath));

    root.addEventListener("arpro:onboarding-open-request", (event) => {
      requestOpen(event.detail?.trigger || null);
    });

    root.addEventListener("arpro:quote-selection-updated", (event) => {
      const entries = Array.isArray(event.detail?.items) ? event.detail.items.slice(0, 24) : [];
      const safeText = (value, maximum = 160) => typeof value === "string" ? value.trim().slice(0, maximum) : "";
      const lines = entries.map((entry, index) => {
        const name = safeText(entry?.name);
        const brand = safeText(entry?.brand, 100);
        const category = safeText(entry?.category, 100);
        return name ? `${index + 1}. ${name} — ${brand} — ${category}` : "";
      }).filter(Boolean);
      if (!lines.length) return;
      if (requestKind) requestKind.value = "Demande de cotation Export AR.Spirits PRO";
      if (quoteSelectionField) quoteSelectionField.value = lines.join("\n");
      if (quoteSelectionSummary) quoteSelectionSummary.textContent = arproUi(lines.length > 1 ? "js_export_selected_many" : "js_export_selected_one", lines.length > 1 ? "{count} références sélectionnées." : "{count} référence sélectionnée.", { count: lines.length });
      if (quoteSelectionPreview) quoteSelectionPreview.hidden = false;
      const reviewOption = Array.from(tariffSelect?.options || []).find((option) => option.dataset.arproRequestedMode === "review");
      if (reviewOption && tariffSelect) tariffSelect.value = reviewOption.value;
      announce(arproUi("js_export_selection_prepared", "Sélection Export préparée. Relisez et complétez le formulaire avant tout envoi."));
    });

    root.addEventListener("arpro:onboarding-dismiss-navigation", markDismissedForNavigation);

    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      closeDialog();
    });

    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener("close", () => {
      if (!document.querySelector(".arpro-onboarding__dialog[open]")) {
        document.documentElement.classList.remove("arpro-onboarding-open");
      }
      if (reopenButton) reopenButton.hidden = false;

      const target = resumeTarget;
      const shouldRestoreFocus = !navigationPending;
      resumeTarget = "";
      navigationPending = false;

      if (target) {
        window.setTimeout(() => scrollToTarget(target), 40);
      } else if (shouldRestoreFocus) {
        const returnTarget = previouslyFocused?.isConnected && isVisible(previouslyFocused)
          && previouslyFocused.matches(focusableSelector)
          && Array.from(previouslyFocused.getClientRects()).some((rect) => rect.width > 0 && rect.height > 0)
          ? previouslyFocused : reopenButton;
        returnTarget?.focus({ preventScroll: true });
        if (document.activeElement !== returnTarget) reopenButton?.focus({ preventScroll: true });
      }
      if (shouldRestoreFocus) announce(arproUi("js_access_dialog_closed", "Fenêtre de demande d’accès fermée."));
    });

    dialog.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return;
      const focusable = getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const configureRegistration = () => {
      if (!registrationInput) return true;
      const isFrench = isFrenchCountry(countryInput?.value || "France");

      if (isFrench) {
        if (registrationLabel) registrationLabel.textContent = arproUi("js_registration_siret_label", "Numéro SIRET");
        if (registrationHint) registrationHint.textContent = arproUi("js_registration_siret_hint", "14 chiffres pour identifier et vérifier précisément votre établissement.");
        if (registrationType) registrationType.value = "SIRET";
        registrationInput.inputMode = "numeric";
        registrationInput.maxLength = 14;
        registrationInput.setAttribute("pattern", "[0-9]{14}");
      } else {
        if (registrationLabel) registrationLabel.textContent = arproUi("js_registration_foreign_label", "Numéro d’immatriculation de l’entreprise");
        if (registrationHint) registrationHint.textContent = arproUi("js_registration_foreign_hint", "Renseignez l’identifiant officiel délivré dans le pays de l’établissement.");
        if (registrationType) registrationType.value = "Immatriculation professionnelle hors France";
        registrationInput.inputMode = "text";
        registrationInput.maxLength = 64;
        registrationInput.removeAttribute("pattern");
      }

      registrationInput.setCustomValidity("");
      registrationInput.setAttribute("aria-invalid", "false");
      return isFrench;
    };

    const validateRegistration = () => {
      if (!registrationInput) return true;
      const isFrench = configureRegistration();
      const value = registrationInput.value.trim();
      const digits = value.replace(/\s/g, "");
      const valid = isFrench
        ? isPlausibleSiret(value)
        : /^(?=.{4,64}$)[\p{L}\p{N}][\p{L}\p{N} .\/-]*$/u.test(value) && !/^(.)\1+$/.test(value);
      const message = isFrench
        ? arproUi("js_registration_siret_invalid", "Saisissez un SIRET de 14 chiffres avec une clé de contrôle valide.")
        : arproUi("js_registration_foreign_invalid", "Saisissez le numéro d’immatriculation officiel de votre entreprise.");

      registrationInput.setCustomValidity(valid ? "" : message);
      registrationInput.setAttribute("aria-invalid", valid ? "false" : "true");
      if (valid && isFrench) registrationInput.value = digits;
      return valid;
    };

    const validateVat = () => {
      if (!vatInput) return true;
      const raw = vatInput.value.trim();
      const value = raw.replace(/[\s.\-]/g, "").toUpperCase();
      const isFrench = isFrenchCountry(countryInput?.value || "France");
      let valid = !raw;
      if (raw && isFrench) {
        valid = /^FR[A-Z0-9]{2}\d{9}$/.test(value)
          && value.slice(-9) === registrationInput?.value.replace(/\s/g, "").slice(0, 9);
        if (valid && /^FR\d{11}$/.test(value)) {
          const key = String((12 + 3 * (Number(value.slice(-9)) % 97)) % 97).padStart(2, "0");
          valid = value.slice(2, 4) === key;
        }
      } else if (raw) {
        // Foreign VAT formats vary: reject malformed input, leave registry checks to ADV.
        const expectedPrefix = vatPrefixForCountry(countryInput?.value || "");
        valid = /^[A-Z]{2}[A-Z0-9]{2,14}$/.test(value)
          && (!expectedPrefix || value.startsWith(expectedPrefix))
          && !/^([A-Z0-9])\1+$/.test(value.slice(2));
      }
      vatInput.setCustomValidity(valid ? "" : isFrench
        ? arproUi("js_vat_french_invalid", "La TVA française doit correspondre au SIREN du SIRET saisi et comporter une clé cohérente.")
        : arproUi("js_vat_foreign_invalid", "Saisissez un numéro de TVA avec un préfixe cohérent avec le pays, ou laissez ce champ vide."));
      vatInput.setAttribute("aria-invalid", valid ? "false" : "true");
      if (valid && raw) vatInput.value = value;
      return valid;
    };

    const validatePhone = () => {
      if (!phoneInput) return true;
      const digits = normalizedDigits(phoneInput.value);
      const valid = digits.length >= 7 && digits.length <= 15;
      phoneInput.setCustomValidity(valid ? "" : arproUi("js_phone_invalid", "Saisissez un numéro de téléphone professionnel valide."));
      phoneInput.setAttribute("aria-invalid", valid ? "false" : "true");
      return valid;
    };

    configureRegistration();
    countryInput?.addEventListener("input", configureRegistration);
    countryInput?.addEventListener("change", configureRegistration);
    registrationInput?.addEventListener("blur", validateRegistration);
    vatInput?.addEventListener("blur", validateVat);
    phoneInput?.addEventListener("blur", validatePhone);

    const lookupMessage = (key) => {
      if (lookupStatus) lookupStatus.textContent = root.dataset[key] || "";
    };

    const invalidateLookup = () => {
      lookupVersion += 1;
      if (lookupController) {
        lookupController.abort();
        lookupController = null;
        lookupMessage("arproLookupChanged");
      } else if (lookupStatus) {
        lookupStatus.textContent = "";
      }
      if (lookupButton) lookupButton.disabled = false;
    };

    registrationInput?.addEventListener("input", invalidateLookup);
    countryInput?.addEventListener("input", invalidateLookup);
    countryInput?.addEventListener("change", invalidateLookup);

    if (lookupContainer && typeof window.fetch === "function" && typeof AbortController === "function") {
      lookupContainer.hidden = false;
    }

    lookupButton?.addEventListener("click", async () => {
      if (lookupController) return;
      const siret = (registrationInput?.value || "").replace(/\s/g, "");
      const country = countryInput?.value || "France";
      if (!isFrenchCountry(country) || !/^\d{14}$/.test(siret)) {
        lookupMessage("arproLookupRequiresSiret");
        registrationInput?.focus();
        return;
      }
      const version = ++lookupVersion;
      const originalValues = Object.fromEntries(Object.entries(lookupFields).map(([key, field]) => [key, field?.value || ""]));
      const originalFieldVersions = { ...lookupFieldVersions };
      const controller = new AbortController();
      lookupController = controller;
      lookupButton.disabled = true;
      lookupMessage("arproLookupLoading");
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL("https://recherche-entreprises.api.gouv.fr/search");
        url.searchParams.set("q", siret);
        url.searchParams.set("minimal", "true");
        url.searchParams.set("include", "siege,matching_etablissements");
        url.searchParams.set("per_page", "1");
        const response = await window.fetch(url.href, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "omit",
          referrerPolicy: "no-referrer",
          cache: "no-store",
          redirect: "error",
          signal: controller.signal
        });
        if (!response.ok) throw new Error("registry_unavailable");
        const payload = await response.json();
        if (version !== lookupVersion || siret !== (registrationInput?.value || "").replace(/\s/g, "") || country !== (countryInput?.value || "France")) return;
        const establishment = findExactEstablishment(payload, siret);
        if (!establishment) {
          lookupMessage("arproLookupNotFound");
          return;
        }
        let filled = 0;
        Object.entries(lookupFields).forEach(([key, field]) => {
          if (!field || originalValues[key].trim() !== "" || field.value !== originalValues[key] || lookupFieldVersions[key] !== originalFieldVersions[key] || !establishment[key]) return;
          field.value = establishment[key];
          field.dispatchEvent(new Event("input", { bubbles: true }));
          filled += 1;
        });
        if (filled > 0) {
          const addressDetails = root.querySelector("[data-arpro-address-details]");
          if (addressDetails) addressDetails.open = true;
        }
        lookupMessage(filled > 0 ? "arproLookupFilled" : "arproLookupPreserved");
      } catch (_error) {
        if (version === lookupVersion) lookupMessage("arproLookupUnavailable");
      } finally {
        window.clearTimeout(timeout);
        if (version === lookupVersion) {
          lookupController = null;
          lookupButton.disabled = false;
        }
      }
    });

    const submittedMode = getTariffMode(tariffSelect);
    const storedMode = readSessionMode(tariffStorageKey);
    let requestWasSubmitted = readSessionFlag(submittedStorageKey);
    if (hasSuccess && storedMode) {
      writeSessionFlag(submittedStorageKey);
      requestWasSubmitted = true;
    }
    if (tariffSelect && submittedMode) {
      writeSessionMode(tariffStorageKey, submittedMode);
      emitTariffRequest(root, submittedMode, requestWasSubmitted);
    } else if (tariffSelect && storedMode) {
      const storedOption = Array.from(tariffSelect.options).find(
        (option) => option.dataset.arproRequestedMode === storedMode
      );
      if (storedOption) {
        tariffSelect.value = storedOption.value;
        emitTariffRequest(root, storedMode, requestWasSubmitted);
      }
    } else if (storedMode) {
      emitTariffRequest(root, storedMode, requestWasSubmitted);
    }

    tariffSelect?.addEventListener("change", () => {
      const mode = getTariffMode(tariffSelect);
      if (!mode) return;
      clearSessionFlag(submittedStorageKey);
      writeSessionMode(tariffStorageKey, mode);
      emitTariffRequest(root, mode, false);
      announce(arproUi("js_tariff_preference_selected", "Préférence tarifaire sélectionnée pour cette session. La demande n’est envoyée qu’avec le formulaire."));
    });

    form?.addEventListener("input", (event) => {
      const field = event.target;
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) return;
      field.setCustomValidity("");
      field.setAttribute("aria-invalid", "false");
    });

    form?.addEventListener(
      "invalid",
      (event) => {
        const field = event.target;
        if (field instanceof HTMLElement) field.setAttribute("aria-invalid", "true");
      },
      true
    );

    form?.addEventListener("submit", (event) => {
      if (root.dataset.arproPrivacyConfigured !== "true") {
        event.preventDefault();
        announce(arproUi("js_access_privacy_configuration_block", "Envoi bloqué : la politique de confidentialité doit être configurée."));
        return;
      }
      const registrationValid = validateRegistration();
      const vatValid = validateVat();
      const phoneValid = validatePhone();
      const mode = getTariffMode(tariffSelect);

      if (mode) {
        clearSessionFlag(submittedStorageKey);
        writeSessionMode(tariffStorageKey, mode);
        emitTariffRequest(root, mode, false);
      }

      if (!registrationValid || !vatValid || !phoneValid || !form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        const firstInvalid = form.querySelector("input:invalid, select:invalid, textarea:invalid");
        firstInvalid?.focus({ preventScroll: false });
        announce(arproUi("js_form_required_corrections", "Certains champs obligatoires doivent être corrigés."));
        return;
      }

      const submitButton = form.querySelector("[data-arpro-onboarding-submit]");
      const submitLabel = submitButton?.querySelector("[data-arpro-submit-label]");
      if (submitButton) submitButton.disabled = true;
      if (submitLabel) submitLabel.textContent = arproUi("js_form_sending", "Envoi en cours…");
      announce(arproUi("js_access_request_sending", "Envoi de votre demande professionnelle en cours."));
    });

    window.addEventListener("pageshow", () => {
      const submitButton = form?.querySelector("[data-arpro-onboarding-submit]");
      const submitLabel = submitButton?.querySelector("[data-arpro-submit-label]");
      if (submitButton && root.dataset.arproPrivacyConfigured === "true") submitButton.disabled = false;
      if (submitLabel && submitLabel.dataset.arproOriginalLabel) submitLabel.textContent = submitLabel.dataset.arproOriginalLabel;
    });
    const initialSubmitLabel = form?.querySelector("[data-arpro-submit-label]");
    if (initialSubmitLabel) initialSubmitLabel.dataset.arproOriginalLabel = initialSubmitLabel.textContent;

    const designMode = root.dataset.arproDesignMode === "true";
    const hashRequestsOnboarding = window.location.hash === "#demande-acces-pro";

    if (hasSuccess || hasErrors || designMode) {
      requestOpen();
    } else if (hashRequestsOnboarding) {
      window.location.replace(accessRequestPath);
    } else if (reopenButton) {
      reopenButton.hidden = false;
    }

    ["visitorConsentCollected", "trackingConsentAccepted", "shopify:customer-privacy-changed"].forEach((eventName) => {
      document.addEventListener(eventName, releaseAfterPrivacy);
    });

    window.addEventListener("hashchange", (event) => {
      if (new URL(event.newURL).hash === onboardingHash) window.location.assign(accessRequestPath);
    });
  };

  const initAll = (scope = document) => {
    if (isAccessRequestPage()) {
      scope.querySelectorAll("[data-arpro-onboarding-open]").forEach((button) => { button.hidden = true; });
      return;
    }
    scope.querySelectorAll("[data-arpro-pro-onboarding]").forEach(initOnboarding);
  };

  document.addEventListener("click", (event) => {
    const source = event.target instanceof Element ? event.target : null;
    if (!source) return;

    const navigationLink = source.closest("[data-arpro-onboarding-dismiss-navigation]");
    if (navigationLink) {
      document.querySelectorAll("[data-arpro-pro-onboarding]").forEach((root) => {
        root.dispatchEvent(new CustomEvent("arpro:onboarding-dismiss-navigation"));
      });
      return;
    }

    const trigger = source.closest("[data-arpro-onboarding-trigger], a[href$='#demande-acces-pro']");
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const root = document.querySelector("[data-arpro-pro-onboarding]");
    if (!root) return;

    event.preventDefault();
    if (!isAccessRequestPage()) window.location.assign(accessRequestPath);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
